import { API_V2 } from '../api';
import { UserClientRoleApi, UserRoleApi, UserAccountStatus } from '@front-app/services/UserApi/UserModel';
import { UserRole } from '@front-app/services/UserApi/UserModel';

export enum REPORT_TYPE {
	ALL_MEMBER_LISTING = 'ALL_MEMBER_LISTING',
	GROUP_MEMBER_LISTING = 'GROUP_MEMBER_LISTING',
	EVENT_MEMBER_LISTING = 'EVENT_MEMBER_LISTING',
	EVENT_BANNERS_REPORT = 'EVENT_BANNERS_REPORT',
	ENGAGEMENT_REPORT = 'ENGAGEMENT_REPORT',
	TOTAL_MEMBERS = 'MEMBERS',
	ACTIVE_MEMBERS = 'ACTIVE_MEMBERS',
	CONTENT_INTERACTIONS = 'CONTENT_INTERACTIONS',
	DISCUSSION_TOPICS = 'DISCUSSION_TOPICS',
	DISCUSSION_POSTS = 'DISCUSSION_POSTS',
	DISCUSSION_VIEWS = 'DISCUSSION_VIEWS',
	FILE_AND_LINK_SHARES = 'SHARES',
	FILE_DOWNLOADS = 'FILE_DOWNLOADS',
	ENGAGEMENT = 'ENGAGEMENT',
}

export enum REPORT_FILTER_TYPE {
	AVATAR = 'AVATAR',
	ROLE = 'ROLE',
	ORGANIZATION = 'ORGANIZATION',
	SPECIALTY = 'SPECIALTY',
	INTERESTS = 'INTERESTS',
	VERIFICATION = 'VERIFICATION',
	LANGUAGE = 'LANGUAGE',
	VISITED_BEFORE = 'VISITED_BEFORE',
	VISITED_AFTER = 'VISITED_AFTER',
}

export enum REPORT_ROLE_FILTER_OPTION {
	PHYSICIAN = 'PHYSICIAN',
	NURSE = 'NURSE',
	PHARMACIST = 'PHARMACIST',
}

export enum DEFAULT_REPORT_OPTION {
	ANY = 'ANY',
	NONE = 'NONE',
	NO_SELECTION = 'NO_SELECTION',
}

export interface CommunityReport {
	key: REPORT_TYPE,
	label: string,
}

export interface CommunityReportRequest {
	type: REPORT_TYPE,
	groupIds: string[],
	startTime: string,
	endTime: string,
}

export interface MemberListingReportRequest {
	hasAvatar: boolean | null,
	roles: string[],
	hasAnyRole: boolean,
	hasNoRoles: boolean,
	hasOrganization: boolean | null,
	specialtyIds: number[],
	hasAnySpecialty: boolean,
	hasNoSpecialties: boolean,
	interestIds: number[],
	hasAnyInterest: boolean,
	hasNoInterests: boolean,
	accountStatuses: string[],
	primaryLanguage: string | null,
}

export interface GroupMembersReportRequest extends MemberListingReportRequest {
	groupIds: string[],
	visitedGroupBefore: string | null,
	visitedGroupAfter: string | null,
}

export interface AllMemberListingReportRequest extends MemberListingReportRequest {
	email: string,
}

export interface GroupsListItem {
	groupId: string,
	groupName: string,
}

export enum GroupsListType {
	ACTIVE = 'active',
	ALL = 'all',
	JOINED = 'joined',
}

export interface GroupReportData {
	groupName: string,
	count: number,
}

export interface GroupEngagementData {
	groupId: string,
	groupName: string,
	data: GroupEngagementDataItem[],
}

export interface GroupEngagementDataItem {
	type: REPORT_TYPE,
	count: number,
}

export interface UserGroupRole {
	groupName: string,
	role: UserRole,
}

export interface UserReportResult {
	id: string,
	salutation: string,
	firstName: string,
	middleName: string,
	lastName: string,
	email: string,
	organizationName: string,
	accountStatus: UserAccountStatus,
	roles: UserRoleApi[],
	specialties: string[],
	interests: string[],
}

export interface GroupReportUser extends UserReportResult {
	groupRoles: UserGroupRole[],
}

export interface UpdateUserAccountStatusRequest {
	userIds: string[],
	status: UserAccountStatus,
}

export interface AddUserRolesRequest {
	userIds: string[],
	roles: UserClientRoleApi[],
}

export function fetchCommunityReport(payload: CommunityReportRequest): Promise<GroupReportData[]> {
	return API_V2.post(`/groups/data/report`, payload)
		.then(({ data }) => data as GroupReportData[]);
}

export function fetchEngagementReport(payload: CommunityReportRequest): Promise<GroupEngagementData[]> {
	return API_V2.post(`/groups/data/engagement`, payload)
		.then(({ data }) => data as GroupEngagementData[]);
}

export function fetchFilteredGroupMembers(payload: GroupMembersReportRequest): Promise<GroupReportUser[]> {
	return API_V2.post(`/groups/data/members`, payload)
		.then(({ data }) => data as GroupReportUser[]);
}

export function fetchAllGroupsList(type: GroupsListType): Promise<GroupsListItem[]> {
	return API_V2.get(`/groups/data/list?type=${type}`)
		.then(({ data }) => data as GroupsListItem[]);
}

export function fetchAllMemberListingReport(payload: AllMemberListingReportRequest): Promise<UserReportResult[]> {
	return API_V2.post('/users/search', payload)
		.then(({ data }) => data as UserReportResult[]);
}

export function changeAccountStatus(payload: UpdateUserAccountStatusRequest): Promise<void> {
	return API_V2.put('/users/status', payload);
}

export function addUserRoles(payload: AddUserRolesRequest): Promise<void> {
	return API_V2.put('/users/add-roles', payload);
}
